import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { blogPosts } from "@/data/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";
import { MarkdownContent } from "@/components/MarkdownContent";
import { RelatedPosts } from "@/components/RelatedPosts";
import { BlogDoneForYouCTA } from "@/components/BlogDoneForYouCTA";
import { NavLink } from "@/components/NavLink";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Calculate estimated reading time
  const calculateReadingTime = (text: string): number => {
    const wordsPerMinute = 200;
    const wordCount = text.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const readingTime = calculateReadingTime(post.content);

  // Remove the first H1 from markdown content to avoid duplicate title
  const stripFirstH1 = (markdown: string): string => {
    // Remove the first line if it starts with # (H1)
    const lines = markdown.split('\n');
    if (lines[0].trim().startsWith('# ')) {
      return lines.slice(1).join('\n').trim();
    }
    return markdown;
  };

  const contentWithoutTitle = stripFirstH1(post.content);

  const fullUrl = `https://www.leafrushmarketing.com/blog/${post.slug}/`;
  const pageTitle = `${post.title} | Leaf Rush Marketing`;

  // Create plain text version of content for schema
  const plainTextContent = post.content
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
    .replace(/`{1,3}[^`]*`{1,3}/g, '')
    .replace(/\n{2,}/g, ' ')
    .trim();

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "articleBody": plainTextContent,
    "datePublished": post.publishDate,
    "dateModified": post.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    },
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Leaf Rush Marketing",
      "url": "https://www.leafrushmarketing.com"
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={fullUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={fullUrl} />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:author" content={post.author} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LeafRushMarketing" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>
      
      <article className="pb-20">
        {/* Hero Image */}
        {post.heroImageUrl && (
          <div className="w-full h-[400px] md:h-[500px] overflow-hidden bg-muted">
            <img 
              src={post.heroImageUrl} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="container px-4 md:px-6 py-8 md:py-12">
          <div className="mx-auto max-w-[720px]">
            <Link to="/blog" className="inline-block mb-8">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Button>
            </Link>

            {/* Post Header */}
            <header className="mb-12 pb-8 border-b">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.2] text-foreground">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={post.publishDate}>
                    {formatDate(post.publishDate)}
                  </time>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{readingTime} min read</span>
                </div>
              </div>
            </header>

            {/* Article Content */}
            <article className="mb-8">
              <MarkdownContent content={contentWithoutTitle} />
            </article>

            {/* Done-For-You CTA Block */}
            <BlogDoneForYouCTA 
              variant={
                post.title.toLowerCase().includes("landscaping") ? "landscaping" :
                post.title.toLowerCase().includes("lawn") ? "lawn-care" : "general"
              }
            />

            {/* Related Posts */}
            <RelatedPosts currentSlug={post.slug} maxPosts={3} />

            {/* CTA Section */}
            <div className="pt-12 border-t mt-12">
              <div className="bg-gradient-to-br from-accent/50 to-accent/30 rounded-2xl p-8 md:p-10 text-center shadow-sm">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  Ready to grow your lawn care business?
                </h3>
                <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
                  Get a high-converting website built specifically for your lawn care or landscaping business
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <NavLink to="/lawn-care-website-design/">
                    <Button size="lg" className="text-lg px-6">
                      Lawn Care Websites
                    </Button>
                  </NavLink>
                  <NavLink to="/landscaping-website-design/">
                    <Button size="lg" variant="outline" className="text-lg px-6">
                      Landscaping Websites
                    </Button>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
